//dependencies
import React from "react";
import { z } from "zod";

//components
import { FormField, FormRadioButtons, PanelFilters } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../../../../components/Inputs/SelectEmployee";

const FormsINeedToDoFilterSchema = z.object({
	subject: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.nullable()
		.optional(),
	status: z.string().optional(),
});

export type FormsINeedToDoFilters = z.infer<typeof FormsINeedToDoFilterSchema>;

export const FormsINeedToDoStatusOptions = [
	{ value: "any", label: "Any" },
	{ value: "fill", label: "Fill out the form" },
	{ value: "sign", label: "Sign off the form" },
	{ value: "awaiting_submission", label: "Awaiting Submission" },
	{ value: "awaiting_sign_off", label: "Awaiting Sign Off" },
];

interface FormsINeedToDoFiltersPanelProps {
	filters?: FormsINeedToDoFilters;
	isOpen: boolean;
	onClose: () => void;
	onApplyFilters: (filters: FormsINeedToDoFilters) => void;
}

export const FormsINeedToDoFiltersPanel = ({
	filters,
	isOpen,
	onClose,
	onApplyFilters,
}: FormsINeedToDoFiltersPanelProps) => {
	const handleSubmit = ({
		value,
	}: {
		value: FormsINeedToDoFilters;
		resetForm: () => void;
		setError: (field: keyof FormsINeedToDoFilters, error: string) => void;
	}) => {
		const formattedState = { ...value };

		// Remove empty, null, or undefined fields
		Object.keys(formattedState).forEach((key) => {
			const filterValue = formattedState[key];
			if (filterValue === null || filterValue === undefined || filterValue === "") {
				delete formattedState[key];
			}
		});

		onApplyFilters(formattedState);
		onClose();
	};

	const defaultValues: FormsINeedToDoFilters = {
		subject: filters?.subject
			? "id" in filters.subject
				? filters.subject
				: {
						id: (filters.subject as { value: string }).value,
						name: (filters.subject as { label: string }).label,
					}
			: null,
		status: filters?.status || "any",
	};

	return (
		<PanelFilters
			isOpen={isOpen}
			onClose={onClose}
			schema={FormsINeedToDoFilterSchema}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		>
			<div className="space-y-4">
				<FormField<{ id: string; name: string } | null>
					id="subject"
					label="Subject"
					input={(field) => (
						<SingleSelectEmployee
							placeholder="Make a selection"
							value={field.value ?? undefined}
							onChange={(employeeId, employeeName) => {
								field.onChange(
									employeeId && employeeName ? { id: employeeId, name: employeeName } : null
								);
							}}
						/>
					)}
				/>

				<FormRadioButtons id="status" label="Status" options={FormsINeedToDoStatusOptions} />
			</div>
		</PanelFilters>
	);
};

export default FormsINeedToDoFiltersPanel;
